import { cn } from "@/lib/utils";
import { Home, ShieldCheck } from "lucide-react";
import { useSidebarCollapse } from "@/hooks/useSidebarCollapse";
import { Logo } from "./Logo";
import { NavItem } from "./NavItem";
import { DraftsSection } from "./sections/DraftsSection";
import { LibrarySection } from "./sections/LibrarySection";
import { RightsSection } from "./sections/RightsSection";
import { CollaborateSection } from "./sections/CollaborateSection";
import { SettingsSection } from "./sections/SettingsSection";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { UserCacheService } from "@/lib/user-cache";
import { useCapabilities } from "@/hooks/useCapabilities";
import {
  ADMIN_MANAGE,
  CATALOG_READ_PUBLISHER_DIRECTORY,
  CATALOG_UPLOAD,
  CATALOG_WRITE,
} from "@/lib/capabilities";
import { NotificationBell } from "./NotificationBell";

export function Sidebar() {
  const { isCollapsed } = useSidebarCollapse();

  const navigate = useNavigate();
  const location = useLocation();
  const [userEmail, setUserEmail] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [profile, setProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Single role/capability resolution for the whole nav (anti-flash: gates wait on loadedOnce).
  const { loadedOnce, subject, has } = useCapabilities();

  useEffect(() => {
    async function fetchUserProfile() {
      try {
        // Use cached user service to prevent duplicate API calls
        const user = await UserCacheService.getUser();
        if (user) {
          setUserEmail(user.email || "");

          // Use cached profile service
          const profile = await UserCacheService.getProfile(user.id);

          if (profile) {
            setProfile(profile);
            setUserName(
              profile.display_name || user.email?.split("@")[0] || "User"
            );
          } else {
            setUserName(user.email?.split("@")[0] || "User");
          }
        }
      } catch (error) {
        setUserName("User");
      } finally {
        setIsLoading(false);
      }
    }

    fetchUserProfile();

    // Listen for profile updates
    const handleProfileUpdate = () => {
      fetchUserProfile();
    };

    // Listen for custom profile update events
    window.addEventListener("profileUpdated", handleProfileUpdate);

    return () => {
      window.removeEventListener("profileUpdated", handleProfileUpdate);
    };
  }, []);

  return (
    <div
      className={cn(
        "h-full shrink-0 bg-card border-r border-border transition-all duration-300 relative",
        isCollapsed ? "w-[4.5rem]" : "w-[240px]"
      )}
    >
      <div className="flex h-full flex-col">
        {/* Header with Logo and Search */}
        <div className="p-4 border-b border-border">
          <Logo isCollapsed={isCollapsed} />
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-6">
          {/* Home — standalone, buyers only */}
          {!loadedOnce ? (
            <div className="space-y-3 px-2 py-1" aria-hidden="true">
              <div className="h-3 w-16 rounded bg-muted/70 animate-pulse" />
              <div className="h-9 rounded-md bg-muted/45 animate-pulse" />
              <div className="h-9 rounded-md bg-muted/35 animate-pulse" />
            </div>
          ) : null}
          {loadedOnce && subject.accountType === "buyer" && (
            <div className="pb-2 border-b border-border/50">
              <NavItem
                icon={Home}
                label="Home"
                path="/home"
                isCollapsed={isCollapsed}
                isActive={location.pathname === "/home"}
              />
              <NavItem
                icon={ShieldCheck}
                label="NDA"
                path="/nda"
                isCollapsed={isCollapsed}
                isActive={location.pathname === "/nda"}
              />
            </div>
          )}
          {/* Drafts: admission surface — seller + admin (CATALOG_UPLOAD). */}
          {loadedOnce && has(CATALOG_UPLOAD) && (
            <DraftsSection
              isCollapsed={isCollapsed}
              currentPath={location.pathname}
            />
          )}
          {/* Library: everyone except negotiators (orientation). Publishers item gated on capability. */}
          {loadedOnce && subject.accountType !== "negotiator" && (
            <LibrarySection
              isCollapsed={isCollapsed}
              currentPath={location.pathname}
              canSeePublishers={has(CATALOG_READ_PUBLISHER_DIRECTORY)}
            />
          )}
          {loadedOnce ? (
            <>
              {/* Rights before Collaborate: you confirm rights, then you trade on them.
                  The SECTION is gated here rather than inside itself because both of its items
                  are non-buyer (`!isBuyer` and `!isBuyer && !isNegotiator`), so for a buyer the
                  section is empty — and a "RIGHTS" heading with nothing under it is worse than
                  no heading at all. Gating at the call site is also the pattern the rest of this
                  nav already uses (Home, Drafts, Library), and it means the section never mounts
                  for a buyer, so its rights-to-confirm fetch never fires for someone who by
                  construction (OD-1) can hold nothing. Its per-item gates stay verbatim inside. */}
              {subject.accountType !== "buyer" && (
                <RightsSection
                  isCollapsed={isCollapsed}
                  currentPath={location.pathname}
                  isBuyer={subject.accountType === "buyer"}
                  /* Your Companies is gated on the CAPABILITY, not on `!isBuyer && !isNegotiator`.
                     Two reasons. It is the same predicate `SellerGuard` uses on the route, so the
                     nav and the route cannot disagree — a visible item that redirects is worse than
                     a hidden one. And capabilities are default-DENY: `accountType` is null when the
                     role fetch errors while `loadedOnce` is already true, and a negative predicate
                     default-ALLOWS there, showing the item to someone the route then bounces. */
                  canManageCompanies={has(CATALOG_WRITE)}
                />
              )}
              <CollaborateSection
                isCollapsed={isCollapsed}
                currentPath={location.pathname}
                isBuyer={subject.accountType === "buyer"}
                isNegotiator={subject.accountType === "negotiator"}
              />
              <SettingsSection
                isCollapsed={isCollapsed}
                isAdmin={has(ADMIN_MANAGE)}
              />
            </>
          ) : null}
        </nav>

        {/* Footer - User Profile */}
        {isCollapsed && (
          <div className="p-2 border-t border-border flex flex-col items-center">
            <NotificationBell
              renderTrigger={(unreadCount) => (
                <div className="relative cursor-pointer">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-muted overflow-hidden hover:ring-2 hover:ring-accent transition-all">
                    {profile?.profile_picture ? (
                      <img
                        src={profile.profile_picture}
                        alt={userName || "User"}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-foreground font-medium text-sm">
                        {userName ? userName[0].toUpperCase() : "U"}
                      </span>
                    )}
                  </div>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </div>
              )}
            />
          </div>
        )}
        {!isCollapsed && (
          <div className="p-4 border-t border-border">
            {isLoading ? (
              <div className="flex items-center gap-3 p-2 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-muted animate-pulse"></div>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="h-3 bg-muted rounded animate-pulse w-20"></div>
                  <div className="h-2 bg-muted rounded animate-pulse w-32"></div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div
                  onClick={() => navigate("/profile")}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition-colors cursor-pointer flex-1 min-w-0"
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-muted overflow-hidden flex-shrink-0">
                    {profile?.profile_picture ? (
                      <img
                        src={profile.profile_picture}
                        alt={userName || "User"}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-foreground font-medium text-sm">
                        {userName ? userName[0].toUpperCase() : "U"}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {userName || "User"}
                    </p>
                    <p className="text-[13px] text-muted-foreground truncate">
                      {userEmail || "user@example.com"}
                    </p>
                  </div>
                </div>
                <NotificationBell />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
